import { Dimensions } from '../../core/types/dimensions.js';
import { NON_OVERLAPPING_HEIGHT } from '../constants.js';
import { HoneycombTileShape } from '../types/honeycomb.js';
import { isEven } from './hive-helpers.js';

/**
 * Asserts how many tiles can fit in the given area with given `tileWidth`
 * and `tileHeight`. Breaks early when drawableElements reach amount of tiles
 * needed.
 *
 * @param tileWidth -
 * @param tileHeight -
 * @param ctnArea -
 * @param shape -
 * @param totalElements - amount of tiles needed
 * @param gap -
 * @returns \{
 *  drawableElements: Amount of tiles that fit with given tileSize in the given Area,
 *  hiveHeight: the height of the resulting hive
 *  rows: amount of rows of the resulting hive
 * \}
 */
export const assertTilesFittingInArea = (
  tileWidth: number,
  tileHeight: number,
  ctnArea: Dimensions,
  shape: HoneycombTileShape,
  totalElements: number,
  gap: number,
): { drawableElements: number; hiveHeight: number } => {
  const { height: ctnHeight, width: ctnWidth } = ctnArea;
  const tilesPerOddRow = Math.floor(ctnWidth / (tileWidth + gap));
  const tilesPerEvenRow = tilesPerOddRow - 1;
  const nonOverlappingHeight = tileHeight * NON_OVERLAPPING_HEIGHT[shape];

  let drawableElements = 0;
  let rows = 0;
  let hiveHeight = 0;
  let heightWithNewRow = tileHeight;

  do {
    ++rows;
    drawableElements += isEven(rows) ? tilesPerEvenRow : tilesPerOddRow;
    hiveHeight = heightWithNewRow;

    if (drawableElements >= totalElements) {
      break;
    }

    heightWithNewRow = hiveHeight + gap + nonOverlappingHeight;
  } while (heightWithNewRow <= ctnHeight);

  return { drawableElements, hiveHeight };
};
